import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  showErrorNotification,
  showSuccessNotification,
} from '../services/common';
import {
  bulkUnsubscribe,
  changePauseDurationPublic,
  pauseEmailPublic,
  reSubscribeManual,
  resubscribeOrganization,
  resubscribePublic,
  saveUnsubscribeReason,
  unSubscribeManual,
  unsubscribeOrganization,
  unsubscribePublic,
} from '../services/unsubscribe';
import {
  changeUnsubscribeEmailTypePreference,
  reSubscribe,
} from '../services/mail';
import { useLocation } from 'react-router';
import qs from 'query-string';

export const useBulkUnsubscribe = () => {
  const queryClient = useQueryClient();
  return useMutation(
    file => {
      const formData = new FormData();
      formData.append('file', file);
      return bulkUnsubscribe(formData);
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(['unsubscriptions']).then(() => {});
        queryClient.invalidateQueries(['unsubscriptionsCount']).then(() => {});
        showSuccessNotification('Bulk Unsubscribe Successfully');
      },
    },
  );
};

export const useUnsubscribeManually = () => {
  const queryClient = useQueryClient();
  return useMutation(unSubscribeManual, {
    onSuccess: async () => {
      queryClient.invalidateQueries(['unsubscriptions']).then(() => {});
      queryClient.invalidateQueries(['unsubscriptionsCount']).then(() => {});
      showSuccessNotification('User has been unsubscribed successfully');
    },
  });
};

export const useResubscribeManually = () => {
  const queryClient = useQueryClient();
  return useMutation(reSubscribeManual, {
    onSuccess: async () => {
      queryClient.invalidateQueries(['unsubscriptions']).then(() => {});
      queryClient.invalidateQueries(['unsubscriptionsCount']).then(() => {});
      showSuccessNotification('User has been re-subscribed successfully');
    },
  });
};

export const useUnsubscribeDomain = () => {
  const queryClient = useQueryClient();
  return useMutation(unsubscribeOrganization, {
    onSuccess: async () => {
      queryClient.invalidateQueries(['unsubscribedDomain']).then(() => {});
      showSuccessNotification('Domain has been unsubscribed successfully');
    },
  });
};

export const useResubscribeDomain = () => {
  const queryClient = useQueryClient();
  return useMutation(resubscribeOrganization, {
    onSuccess: async () => {
      queryClient.invalidateQueries(['unsubscribedDomain']).then(() => {});
      showSuccessNotification('Domain has been re-subscribed successfully');
    },
  });
};

export const useResubscribe = () => {
  return useMutation(reSubscribe, {
    onError: () => {
      showErrorNotification(
        'Error happened while submitting. Please try again later',
      );
    },
  });
};

export const useChangeUnsubscriptionEmailTypePreferences = () => {
  return useMutation(changeUnsubscribeEmailTypePreference, {
    onSuccess: async () => {
      showSuccessNotification('Update unsubscription preference successfully');
    },
  });
};

type UnsubscribeOrPauseData = {
  pauseDuration: number,
  emailTypeId: number,
  isPaused: boolean,
  reason: string,
  otherReason: string,
};

export const useUnsubscribeOrPause = () => {
  const location = useLocation();
  const queryClient = useQueryClient();
  return useMutation(
    async (data: UnsubscribeOrPauseData) => {
      const currentParams = qs.parse(location.search);
      if (data.isPaused) {
        await pauseEmailPublic({
          ...currentParams,
          ['no-redirect']: true,
        });
      } else {
        await unsubscribePublic({
          ...currentParams,
          ['no-redirect']: true,
        });
      }
      const promises = [];
      // If user selected other than 1 month
      if (data.isPaused && data.pauseDuration && data.pauseDuration !== 1) {
        promises.push(
          changePauseDurationPublic({
            ...currentParams,
            duration: data.pauseDuration,
          }),
        );
      }
      if (data.emailTypeId) {
        promises.push(
          changeUnsubscribeEmailTypePreference({
            ...currentParams,
            emailTypeId: data.emailTypeId,
          }),
        );
      }
      if (data.reason || data.otherReason) {
        promises.push(
          saveUnsubscribeReason({
            ...currentParams,
            reason: data.otherReason || data.reason,
          }),
        );
      }
      await Promise.all(promises);
      await queryClient.invalidateQueries('unsubscriptionStatus');
    },
    {
      onError: () => {
        showErrorNotification(
          'Error happened while submitting. Please try again later',
        );
      },
    },
  );
};

export const useChangeUnsubscribeReason = () => {
  const location = useLocation();
  const currentParams = qs.parse(location.search);
  return useMutation(
    reason =>
      saveUnsubscribeReason({
        ...currentParams,
        reason,
      }),
    {
      onError: () => {
        showErrorNotification(
          'Error happened while submitting. Please try again later',
        );
      },
    },
  );
};

export const useResubscribePublic = () => {
  const location = useLocation();
  const currentParams = qs.parse(location.search);
  return useMutation(
    () => {
      return resubscribePublic(currentParams);
    },
    {
      onError: () => {
        showErrorNotification(
          'Error happened while submitting. Please try again later',
        );
      },
    },
  );
};
